import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../../components/BreadCrumbs';
import { toast } from 'react-toastify';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik, FieldArray } from 'formik';
import Button from '../../components/buttons/Button';
import InputField from '../../components/inputFields/InputField';
import Dropdown from '../../components/inputFields/DropDown';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { patchShipment, postShipment, searchOrder, deleteShipment, trackIndianPost } from '../../services/shipment';
import ImagePicker from '../../components/inputFields/ImagePicker';
import AsyncFormikDropdown from '../../components/inputFields/AsyncFormikDropDown';
import { formatDateTime } from '../../utils/FormatDateTime';
import { FiTrash2 } from 'react-icons/fi';
import { FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { MdLocalShipping } from 'react-icons/md';

const PREDEFINED_PROVIDERS = [
    { label: "Indian Post", value: "Indian Post" },
    { label: "Indian Post - Speed", value: "Indian Post - Speed" },
    { label: "Envia", value: "Envia" },
    { label: "Transport", value: "Transport" },
    { label: "Office Pick Up", value: "Office Pick Up" },
    { label: "Bus", value: "Bus" },
    { label: "Other", value: "Other" }
];

const isIndianPostFamily = (providerSelect, providerCustom = '') => {
    const p = providerSelect || providerCustom || '';
    return p === 'Indian Post' || p === 'Indian Post - Speed';
};

/** MySpeedPost tracking URL; user adds consignment no. after `n=` */
const INDIAN_POST_TRACKING_LINK_BASE = "https://myspeedpost.com/?n=";

const getTodayDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getEstimatedDeliveryDate = (shippedDate) => {
    if (!shippedDate) return "";
    const date = new Date(shippedDate);
    date.setDate(date.getDate() + 7);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const STATUS_CHOICES = [
    { label: "Created", value: "CREATED" },
    { label: "Shipped", value: "SHIPPED" },
    { label: "In Transit", value: "IN_TRANSIT" },
    { label: "Delivered", value: "DELIVERED" },
    { label: "Return To Origin", value: "RTO" },
    { label: "Cancelled", value: "CANCELLED" },
];

const defaultShipment = {
    provider_select: "",
    provider: "",
    status: "CREATED",
    tracking_link: "",
    tracking_number: "",
    cost: "",
    cod_commission: "0",
    length: "",
    width: "",
    height: "",
    weight: "",
    estimated_delivery_date: getEstimatedDeliveryDate(getTodayDate()),
    shipped_date: getTodayDate(),
    delivery_date: "",
    parcel_image1: null,
    parcel_image2: null,
    parcel_image3: null,
    parcel_image4: null,
};

function AddShipment({ orderData = null, onClose = null, onSaved = null, isDrawerMode = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(isDrawerMode ? orderData : null);
    const [orderOptions, setOrderOptions] = useState([]);
    const [selectedOrderOption, setSelectedOrderOption] = useState(null);
    const [expandedShipments, setExpandedShipments] = useState({});

    // Indian Post Tracking State
    const [trackingModal, setTrackingModal] = useState({ open: false, data: null, loading: false, trackingNumber: null });

    const handleTrackIndianPost = async (trackingNumber) => {
        setTrackingModal({ open: true, data: null, loading: true, trackingNumber });
        try {
            const res = await trackIndianPost(trackingNumber);
            const payload = res.data?.data || res.data;
            setTrackingModal({ open: true, data: payload, loading: false, trackingNumber });
        } catch (err) {
            toast.error("Tracking info unavailable. Try again later.");
            setTrackingModal({ open: false, data: null, loading: false, trackingNumber: null });
        }
    };

    const closeTrackingModal = () => setTrackingModal({ open: false, data: null, loading: false, trackingNumber: null });

    useEffect(() => {
        if (isDrawerMode) {
            setRowData(orderData);
        } else if (location.state) {
            setRowData(location.state);
        }
    }, [location, orderData, isDrawerMode]);

    const handleDiscardChanges = (resetForm) => {
        resetForm();
        if (isDrawerMode && onClose) {
            onClose();
        } else {
            navigate(-1);
        }
    };

    const getInitialValues = () => {
        if (rowData) {
            const mapLogistic = (log) => {
                let isPredefined = PREDEFINED_PROVIDERS.some(p => p.value === log.provider);
                return {
                    ...defaultShipment,
                    ...log,
                    provider_select: log.provider ? (isPredefined ? log.provider : "Other") : "",
                    provider: log.provider || "",
                    shipped_date: log.shipped_date || getTodayDate(),
                    estimated_delivery_date: log.estimated_delivery_date || getEstimatedDeliveryDate(log.shipped_date || getTodayDate()),
                };
            };

            if (Array.isArray(rowData.logistic)) {
                const mapped = rowData.logistic.map(mapLogistic);
                // From Orders mapping where logistic is an array
                return {
                    order: rowData.id,
                    shipments: mapped.length > 0 ? mapped : [{ ...defaultShipment }],
                };
            } else if (rowData.logistics) {
                // If mapping from Shipments.jsx which passes item in logistics obj
                if (Array.isArray(rowData.logistics)) {
                    const mapped = rowData.logistics.map(mapLogistic);
                    return {
                        order: rowData.id,
                        shipments: mapped.length > 0 ? mapped : [{ ...defaultShipment }],
                    };
                }
                return {
                    order: rowData.id || rowData.logistics.order,
                    shipments: [mapLogistic(rowData.logistics)],
                };
            }
        }
        return {
            order: "",
            shipments: [{ ...defaultShipment }],
        };
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        try {
            const orderId = rowData?.id ?? values.order;

            // Loop through all shipments and save sequentially
            for (const shipment of values.shipments) {
                const data = new FormData();

                // Append standard fields
                data.append('order', orderId);
                data.append('provider', shipment.provider_select === "Other" ? shipment.provider : shipment.provider_select);
                data.append('status', shipment.status || "CREATED");
                data.append('tracking_number', shipment.tracking_number || "null");
                data.append('tracking_link', shipment.tracking_link || "");
                data.append('cost', shipment.cost || 0);
                data.append('cod_commission', shipment.cod_commission || 0);
                data.append('length', shipment.length || 0);
                data.append('width', shipment.width || 0);
                data.append('height', shipment.height || 0);
                data.append('weight', shipment.weight || 0);
                const shippedDate = shipment.shipped_date || getTodayDate();
                const estimatedDeliveryDate = shipment.estimated_delivery_date || getEstimatedDeliveryDate(shippedDate);
                if (estimatedDeliveryDate) data.append('estimated_delivery_date', estimatedDeliveryDate);
                data.append('shipped_date', shippedDate);
                if (shipment.delivery_date) data.append('delivery_date', shipment.delivery_date);

                // Append images
                if (shipment.parcel_image1 && typeof shipment.parcel_image1 === "object") {
                    data.append("parcel_image1", shipment.parcel_image1);
                }
                if (shipment.parcel_image2 && typeof shipment.parcel_image2 === "object") {
                    data.append("parcel_image2", shipment.parcel_image2);
                }
                if (shipment.parcel_image3 && typeof shipment.parcel_image3 === "object") {
                    data.append("parcel_image3", shipment.parcel_image3);
                }
                if (shipment.parcel_image4 && typeof shipment.parcel_image4 === "object") {
                    data.append("parcel_image4", shipment.parcel_image4);
                }

                // Call Post or Patch
                let response;
                if (shipment.id) {
                    response = await patchShipment(data, shipment.id);
                } else {
                    response = await postShipment(data);
                }

                if (response?.status !== 201 && response?.status !== 200) {
                    throw new Error(`Failed to save shipment ${shipment.id || "new"}`);
                }
            }

            toast.success("Shipments saved successfully!");

            if (onSaved) {
                await onSaved({ orderId });
            }

            if (isDrawerMode && onClose) {
                onClose();
            } else {
                navigate(-1);
            }
        } catch (error) {
            let errors = JSON.stringify(error?.response?.data || error.message) || "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    const validationSchema = useMemo(() => Yup.object({
        shipments: Yup.array().of(
            Yup.object().shape({
                provider_select: Yup.string().required("Logistics Partner is required"),
                provider: Yup.string().when('provider_select', {
                    is: "Other",
                    then: () => Yup.string().required("Please specify the Logistics Partner"),
                    otherwise: () => Yup.string().notRequired(),
                }),
                cost: Yup.string().notRequired(),
                cod_commission: Yup.string().notRequired(),
                length: Yup.string().notRequired(),
                width: Yup.string().notRequired(),
                height: Yup.string().notRequired(),
                weight: Yup.string().notRequired(),
            })
        ),
        order: rowData ? Yup.string().notRequired() : Yup.string().required("Order is required"),
    }), [rowData]);

    return (<>
        {!isDrawerMode && (
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                {
                    name: rowData?.fromPage === 'shipments' ? 'Shipments' : 'Orders',
                    link: rowData?.fromPage === 'shipments' ? '/shipments' : '/orders'
                },
                { name: rowData ? 'Update Shipment' : 'Add Shipment' },
            ]} />
        )}
        <div className="mt-8">
            <Formik
                initialValues={getInitialValues()}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, resetForm, values, setFieldValue, touched, errors }) => {
                    const totalCost = values.shipments.reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0);
                    const totalCommission = values.shipments.reduce((sum, s) => sum + (parseFloat(s.cod_commission) || 0), 0);
                    const totalShipments = values.shipments.length;

                    return (
                    <Form>
                        <div className="flex flex-col md:flex-row gap-1 items-center justify-between">
                            <div className="flex flex-col md:flex-row gap-1 items-center">
                                <StyledHeadingText text={rowData && (rowData?.logistic?.length > 0 || rowData?.logistics) ? 'Update Shipment' : 'Add Shipment'} TextColor={'text-primary'} />
                                <StyledHeadingText text={rowData ? `For Order: ${rowData.id} | User: ${rowData.user} | Order Date: ${rowData.order_date}` : ""} size={"text-sm font-normal"} />
                                <StyledHeadingText text={selectedOrderOption ? `For Order: ${selectedOrderOption.id} | User: ${selectedOrderOption.farmer_name} | Order Date: ${formatDateTime(selectedOrderOption.order_date)}` : ""} size={"text-sm font-normal"} />
                            </div>
                        </div>

                        {/* ── Cost Summary Bar ── */}
                        <div className="grid grid-cols-3 gap-3 mt-3 mb-1">
                            <div className="flex flex-col items-center justify-center bg-blue-50 border border-blue-200 rounded-xl py-3 px-4">
                                <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide mb-0.5">Shipments</p>
                                <p className="text-xl font-bold text-blue-700">{totalShipments}</p>
                            </div>
                            <div className="flex flex-col items-center justify-center bg-green-50 border border-green-200 rounded-xl py-3 px-4">
                                <p className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-0.5">Total Logistic Cost</p>
                                <p className="text-xl font-bold text-green-700">₹{totalCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                            <div className="flex flex-col items-center justify-center bg-orange-50 border border-orange-200 rounded-xl py-3 px-4">
                                <p className="text-xs text-orange-500 font-semibold uppercase tracking-wide mb-0.5">Total COD Commission</p>
                                <p className="text-xl font-bold text-orange-700">₹{totalCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                        </div>

                        {!rowData && (
                            <div className="border border-gray-300 mt-5 p-4 rounded-lg bg-gray-100 flex flex-col md:flex-row gap-4">
                                <AsyncFormikDropdown
                                    label="Order"
                                    name="order"
                                    placeholder="Search Order"
                                    apiCallFunction={async (input) => {
                                        const response = await searchOrder(input);
                                        setOrderOptions(response.data.results);
                                        return response.data.results.map((ord) => ({
                                            value: ord.id,
                                            label: `${ord.farmer_name} | ${ord.farmer_mobile_number} | ${formatDateTime(ord.order_date)} | ${ord.id}`,
                                        }));
                                    }}
                                    required
                                    needSearch={true}
                                    onChange={(selectedOption) => {
                                        const selectedOrder = orderOptions.find(
                                            (item) => item.id === selectedOption.value
                                        );
                                        if (selectedOrder) {
                                            setSelectedOrderOption(selectedOrder);
                                        }
                                        setFieldValue("order", selectedOption.value);
                                    }}
                                />
                            </div>
                        )}

                        <FieldArray name="shipments">
                            {({ push, remove }) => (
                                <div>
                                    {values.shipments.map((shipment, index) => (
                                        <div key={index} className="border border-gray-300 mt-5 p-4 py-8 rounded-lg bg-gray-100 flex flex-col gap-4 relative">
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="font-semibold text-lg text-gray-700">Shipment {index + 1} {shipment.id && <span className="text-sm font-normal text-gray-500 ml-2">(ID: {shipment.id})</span>}</h3>
                                                {values.shipments.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (shipment.id) {
                                                                if (window.confirm("Are you sure you want to delete this shipment permanently?")) {
                                                                    deleteShipment(shipment.id)
                                                                        .then(() => {
                                                                            toast.success("Shipment deleted successfully!");
                                                                            remove(index);
                                                                        })
                                                                        .catch(error => {
                                                                            let errors = JSON.stringify(error?.response?.data || error.message) || "Failed to delete shipment.";
                                                                            toast.error(errors);
                                                                        });
                                                                }
                                                            } else {
                                                                remove(index);
                                                            }
                                                        }}
                                                        className="text-red-500 hover:text-red-700 bg-white border border-red-500 rounded p-1"
                                                        title="Remove Shipment"
                                                    >
                                                        <FiTrash2 size={20} />
                                                    </button>
                                                )}
                                                {/* Track with India Post */}
                                                {(isIndianPostFamily(shipment.provider_select, shipment.provider)) &&
                                                    shipment.tracking_number &&
                                                    shipment.tracking_number !== 'null' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleTrackIndianPost(shipment.tracking_number)}
                                                            className="flex items-center gap-1 text-white bg-orange-500 hover:bg-orange-600 border border-orange-600 rounded px-3 py-1 text-sm font-medium"
                                                            title="Track via India Post"
                                                        >
                                                            <MdLocalShipping size={16} /> Track (India Post)
                                                        </button>
                                                    )}
                                            </div>

                                            <div className="flex flex-col w-full">
                                                {/* Basic fields: always visible */}
                                                <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                                                    <div className="flex flex-col">
                                                        <Dropdown
                                                            label="Logistics Partner"
                                                            name={`shipments.${index}.provider_select`}
                                                            options={PREDEFINED_PROVIDERS}
                                                            placeholder="Select Provider"
                                                            value={shipment.provider_select}
                                                            onChange={(val) => {
                                                                setFieldValue(`shipments.${index}.provider_select`, val);
                                                                if (val !== "Other") {
                                                                    setFieldValue(`shipments.${index}.provider`, "");
                                                                }
                                                                if (isIndianPostFamily(val, '')) {
                                                                    const cur = (shipment.tracking_link || "").trim();
                                                                    if (!cur.startsWith(INDIAN_POST_TRACKING_LINK_BASE)) {
                                                                        setFieldValue(`shipments.${index}.tracking_link`, INDIAN_POST_TRACKING_LINK_BASE);
                                                                    }
                                                                }
                                                            }}
                                                            required
                                                        />
                                                        {touched?.shipments?.[index]?.provider_select && errors?.shipments?.[index]?.provider_select ? (
                                                            <div className="text-red-500 text-sm mt-1">{errors.shipments[index].provider_select}</div>
                                                        ) : null}
                                                    </div>

                                                    {shipment.provider_select === "Other" && (
                                                        <InputField
                                                            label="Custom Provider"
                                                            name={`shipments.${index}.provider`}
                                                            type="text"
                                                            placeholder="Enter Provider Name"
                                                            required
                                                        />
                                                    )}

                                                    <InputField
                                                        label="Tracking Link"
                                                        name={`shipments.${index}.tracking_link`}
                                                        type="url"
                                                        placeholder={
                                                            isIndianPostFamily(shipment.provider_select, shipment.provider)
                                                                ? `${INDIAN_POST_TRACKING_LINK_BASE}…consignment no.`
                                                                : "https://xyz.com"
                                                        }
                                                    />
                                                </div>

                                                {/* Rare fields: expandable */}
                                                <button
                                                    type="button"
                                                    className="mt-3 w-full flex items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                                    onClick={() => setExpandedShipments((prev) => ({ ...prev, [index]: !(prev[index] ?? false) }))}
                                                >
                                                    <span>Additional Details</span>
                                                    <span className={`transition-transform duration-200 ${(expandedShipments[index] ?? false) ? "rotate-180" : ""}`}>▼</span>
                                                </button>

                                                {(expandedShipments[index] ?? false) && (
                                                    <>
                                                <div className="grid gap-2 grid-cols-1 md:grid-cols-4 mt-3">
                                                    <InputField
                                                        label="Tracking No."
                                                        name={`shipments.${index}.tracking_number`}
                                                        type="text"
                                                        placeholder="XXXXXXXXXXXXX"
                                                    />
                                                    <InputField
                                                        label="Cost"
                                                        name={`shipments.${index}.cost`}
                                                        type="text"
                                                        placeholder="123"
                                                    />
                                                    <InputField
                                                        label="COD Commission"
                                                        name={`shipments.${index}.cod_commission`}
                                                        type="text"
                                                        placeholder="0"
                                                    />
                                                    <Dropdown
                                                        label="Status"
                                                        name={`shipments.${index}.status`}
                                                        options={STATUS_CHOICES}
                                                        placeholder="Select Status"
                                                        value={shipment.status}
                                                        onChange={(val) => {
                                                            setFieldValue(`shipments.${index}.status`, val);
                                                        }}
                                                    />
                                                </div>

                                                <div className="grid gap-2 grid-cols-1 md:grid-cols-3 mt-4">
                                                    <InputField
                                                        label="Shipping Date"
                                                        name={`shipments.${index}.shipped_date`}
                                                        type="date"
                                                        placeholder="YYYY-MM-DD"
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setFieldValue(`shipments.${index}.shipped_date`, val);
                                                            if (val) {
                                                                setFieldValue(`shipments.${index}.estimated_delivery_date`, getEstimatedDeliveryDate(val));
                                                            } else {
                                                                setFieldValue(`shipments.${index}.estimated_delivery_date`, '');
                                                            }
                                                        }}
                                                    />
                                                    <InputField
                                                        label="Estimated Delivery Date"
                                                        name={`shipments.${index}.estimated_delivery_date`}
                                                        type="date"
                                                        placeholder="YYYY-MM-DD"
                                                    />
                                                    <InputField
                                                        label="Actual Delivery Date"
                                                        name={`shipments.${index}.delivery_date`}
                                                        type="date"
                                                        placeholder="YYYY-MM-DD"
                                                    />
                                                </div>

                                                <div className="grid gap-2 grid-cols-1 md:grid-cols-4 mt-4">
                                                    <InputField
                                                        label="Length (In cm)"
                                                        name={`shipments.${index}.length`}
                                                        type="text"
                                                        placeholder="XX"
                                                    />
                                                    <InputField
                                                        label="Width (In cm)"
                                                        name={`shipments.${index}.width`}
                                                        type="text"
                                                        placeholder="YY"
                                                    />
                                                    <InputField
                                                        label="Height (In cm)"
                                                        name={`shipments.${index}.height`}
                                                        type="text"
                                                        placeholder="ZZ"
                                                    />
                                                    <InputField
                                                        label="Weight (in KGs)"
                                                        name={`shipments.${index}.weight`}
                                                        type="text"
                                                        placeholder="CC"
                                                    />
                                                </div>

                                                <div className="grid gap-2 grid-cols-1 md:grid-cols-4 mt-4">
                                                    <div className="h-48 aspect-square mx-2">
                                                        <ImagePicker
                                                            preloadedImage={shipment.parcel_image1}
                                                            onImageSelect={(file) => setFieldValue(`shipments.${index}.parcel_image1`, file)}
                                                        />
                                                    </div>
                                                    <div className="h-48 aspect-square mx-2">
                                                        <ImagePicker
                                                            preloadedImage={shipment.parcel_image2}
                                                            onImageSelect={(file) => setFieldValue(`shipments.${index}.parcel_image2`, file)}
                                                        />
                                                    </div>
                                                    <div className="h-48 aspect-square mx-2">
                                                        <ImagePicker
                                                            preloadedImage={shipment.parcel_image3}
                                                            onImageSelect={(file) => setFieldValue(`shipments.${index}.parcel_image3`, file)}
                                                        />
                                                    </div>
                                                    <div className="h-48 aspect-square mx-2">
                                                        <ImagePicker
                                                            preloadedImage={shipment.parcel_image4}
                                                            onImageSelect={(file) => setFieldValue(`shipments.${index}.parcel_image4`, file)}
                                                        />
                                                    </div>
                                                </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    <div className="py-5 flex justify-between gap-6 items-center">
                                        <Button
                                            type="button"
                                            className="text-white bg-secondary hover:bg-secondary-dark font-sm rounded-lg text-lg px-8 py-1.5 text-center"
                                            onClick={() => {
                                                const newIndex = values.shipments.length;
                                                push({ ...defaultShipment });
                                                setExpandedShipments((prev) => ({ ...prev, [newIndex]: false }));
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            + Add New Shipment
                                        </Button>

                                        <div className="flex gap-4">
                                            <Button
                                                type="button"
                                                className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg px-8 py-1.5 text-center border border-primary"
                                                onClick={() => handleDiscardChanges(resetForm)}
                                                disabled={isSubmitting}
                                            >
                                                Discard Changes
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg px-8 py-1.5 text-center"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? "Saving..." : "Save"}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </FieldArray>
                    </Form>
                    );
                }}
            </Formik>
        </div>

        {/* Indian Post Tracking Modal */}
        {trackingModal.open && (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={(e) => { if (e.target === e.currentTarget) closeTrackingModal(); }}
            >
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b bg-orange-50 rounded-t-xl">
                        <div className="flex items-center gap-2">
                            <MdLocalShipping size={22} className="text-orange-600" />
                            <div>
                                <h2 className="font-bold text-gray-800 text-base">India Post Tracking</h2>
                                <p className="text-xs text-gray-500">{trackingModal.trackingNumber}</p>
                            </div>
                        </div>
                        <button onClick={closeTrackingModal} className="text-gray-400 hover:text-gray-600 p-1">
                            <FaTimes size={18} />
                        </button>
                    </div>

                    <div className="p-5">
                        {trackingModal.loading ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-3">
                                <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-gray-500 text-sm">Fetching tracking info...</p>
                            </div>
                        ) : trackingModal.data ? (
                            <div>
                                {/* Summary Cards */}
                                <div className="grid grid-cols-2 gap-3 mb-5">
                                    <div className="bg-orange-50 rounded-lg p-3">
                                        <p className="text-xs text-gray-500 mb-1">Current Status</p>
                                        <p className="font-semibold text-orange-700 text-sm">{trackingModal.data.currentStatus || trackingModal.data.status || '—'}</p>
                                    </div>
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <p className="text-xs text-gray-500 mb-1">Est. Delivery</p>
                                        <p className="font-semibold text-blue-700 text-sm">{trackingModal.data.estimatedDelivery || '—'}</p>
                                    </div>
                                    {trackingModal.data.origin && (
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <p className="text-xs text-gray-500 mb-1">Origin</p>
                                            <p className="font-semibold text-gray-700 text-sm">{trackingModal.data.origin}</p>
                                        </div>
                                    )}
                                    {trackingModal.data.destination && (
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <p className="text-xs text-gray-500 mb-1">Destination</p>
                                            <p className="font-semibold text-gray-700 text-sm">{trackingModal.data.destination}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Timeline */}
                                {(trackingModal.data.history || trackingModal.data.events || []).length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-gray-700 text-sm mb-3">Tracking History</h3>
                                        <div className="relative">
                                            {(trackingModal.data.history || trackingModal.data.events || []).map((event, idx) => (
                                                <div key={idx} className="flex gap-3 mb-4 relative">
                                                    {/* Vertical line */}
                                                    {idx < (trackingModal.data.history || trackingModal.data.events || []).length - 1 && (
                                                        <div className="absolute left-[11px] top-6 w-0.5 h-full bg-orange-200" />
                                                    )}
                                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center mt-0.5 z-10">
                                                        <FaMapMarkerAlt size={10} className="text-white" />
                                                    </div>
                                                    <div className="flex-1 bg-gray-50 rounded-lg p-3">
                                                        <p className="font-medium text-gray-800 text-sm">{event.status || event.description}</p>
                                                        {event.location && <p className="text-xs text-gray-500 mt-0.5">📍 {event.location}</p>}
                                                        <p className="text-xs text-gray-400 mt-0.5">{event.timestamp ? new Date(event.timestamp).toLocaleString('en-IN') : event.date}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-center text-gray-500 py-8">No tracking data available.</p>
                        )}
                    </div>
                </div>
            </div>
        )}
    </>);
}

export default AddShipment;